import { ErrorHandler, Injectable, inject } from '@angular/core';
import { ToastService } from 'app/services/toast.service';

// ─────────────────────────────────────────────────────────────────────────────
// Global Error Handler
//
// Catches uncaught exceptions and unhandled promise rejections that escape
// component-level try/catch blocks. Logs them and shows a generic toast
// so the user knows something went wrong.
//
// This does NOT replace component-level error handling — it's a safety net.
// ─────────────────────────────────────────────────────────────────────────────

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
    private toast = inject(ToastService);

    // Throttle: don't spam toasts if many errors fire at once
    private lastToastTime = 0;
    private readonly TOAST_THROTTLE_MS = 3_000;

    handleError(error: unknown): void {
        // Extract useful info
        const message = error instanceof Error ? error.message : String(error);
        const stack = error instanceof Error ? error.stack : undefined;

        // Always log to console for debugging
        console.error('[GlobalErrorHandler] Uncaught error:', message);
        if (stack) {
            console.error(stack);
        }

        // Skip known non-actionable errors
        if (this.isIgnorable(message)) return;

        // Throttled toast — don't flood the user
        const now = Date.now();
        if (now - this.lastToastTime > this.TOAST_THROTTLE_MS) {
            this.lastToastTime = now;
            this.toast.error('Something went wrong. Please try again.');
        }
    }

    /** Filter out noise — framework/browser errors that aren't useful to show */
    private isIgnorable(message: string): boolean {
        const lower = message.toLowerCase();

        // Angular router navigation cancellation (user clicked away)
        if (lower.includes('navigation cancelled')) return true;

        // ResizeObserver loop — benign browser quirk
        if (lower.includes('resizeobserver loop')) return true;

        // Loading chunk failed — will be caught by lazy-load retry or user refresh
        if (lower.includes('loading chunk') && lower.includes('failed')) return true;

        return false;
    }
}
